import React from 'react'
import useBedbankRates from 'hooks/useBedbankRates'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import getBedbankPromoExtraValue, { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import OfferPageStickyFooter from './OfferPageStickyFooter'
import WalledContent from 'components/Common/WalledContent'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import { pluralizeToString } from 'lib/string/pluralize'
import { useOfferPrice } from 'hooks/Offers/useOfferPrice'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import { connect } from 'react-redux'
import { checkCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import PriceRowLuxPlusPrice from 'components/Luxkit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import getValueOffPercent from 'lib/offer/getValueOffPercent'

interface MappedProps {
  canViewLuxPlusBenefits: boolean;
}

interface Props extends MappedProps {
  duration: number;
  offer: App.BedbankOffer;
  rooms: Array<App.Occupants>;
  checkInDate?: string;
  checkOutDate?: string;
  flightPrice?: number;
  flightsEnabled: boolean;
  onScrollTo: () => void;
}

function BedbankOfferPageStickyFooter(props: Props) {
  const {
    offer,
    rooms,
    checkInDate,
    checkOutDate,
    duration,
    flightPrice,
    flightsEnabled,
    onScrollTo,
    canViewLuxPlusBenefits,
  } = props

  const [{
    hotelOnlyRates,
    hotelBundleRates,
  }, fetching] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)
  const rate = hotelOnlyRates[0]
  const bundleRate = hotelBundleRates[0] || rate

  const sell = useBedbankOfferSell(offer, duration, rooms)

  const hotelPrice = rate ? rate.totals.inclusive : sell?.price ?? 0
  const hotelMemberPrice = rate ? rate.totals.memberInclusive : 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice

  const hotelNights = rate?.nights ?? sell?.los

  const propertyFees = rate?.totals?.propertyFees ?? sell?.propertyFees ?? 0
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees

  let hotelValue: undefined | number
  let bundleValue: undefined | number

  let hotelDiscount: number
  let hotelMemberDiscount: number = 0

  const promo = useBedbankRoomRatePromotions(offer.promotions, checkInDate, checkOutDate)
  if (rate) {
    const pkg = rate.room
    const isPromoEnabledRate = !!promo[rate.id]

    if (isPromoEnabledRate) {
      const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
        price: pkg.sell.price,
        value: pkg.sell.value,
        minLos: pkg.sell.los,
        duration,
      }) : 0
      hotelValue = inclusionsAmount + rate.totals.inclusive
      bundleValue = inclusionsAmount + (bundleRate?.totals.inclusive || hotelValue)
      hotelDiscount = getValueOffPercent(hotelValue, inclusionsAmount)
    } else {
      hotelValue = rate.value
      bundleValue = bundleRate?.value || hotelValue
      hotelDiscount = rate.discount
      hotelMemberDiscount = rate.memberDiscount
    }
  } else {
    hotelValue = sell?.value
    bundleValue = sell?.value
    hotelDiscount = sell?.discountPercentage ?? 0
  }

  const datesSelected = !!(checkInDate && checkOutDate)
  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    hotelDiscount,
    hotelMemberDiscount,
    flightPrice,
    checkIn: checkInDate,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })

  const offerPrice = promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price
  const offerMemberPrice = promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice
  const isSoldOut = hotelOnlyRates.length == 0 && datesSelected

  const shownValue = promoteAsBundle ? bundleValue : hotelValue

  const {
    saleUnit,
    total: displayOfferPrice,
    memberTotal: displayMemberPrice,
  } = useOfferPrice({
    value: shownValue,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
    saleUnit: offer.displayPricingAsPerPerson ? 'person' : 'room',
    duration: hotelNights,
    total: offerPrice,
    memberTotal: offerMemberPrice,
  })

  const displayPrice = ((displayMemberPrice ?? 0) > 0 && canViewLuxPlusBenefits) ? displayMemberPrice : displayOfferPrice

  const { shouldShowValue, shouldShowDiscountPercent, highestDiscountPercent } = getHotelOfferDisplayedValue(false, displayPrice ?? 0, hotelValue ?? 0, true)

  if (isSoldOut) {
    return null
  }

  return (
    <OfferPageStickyFooter visible callToActionCopy="View options" offer={offer} onCTAClick={onScrollTo} hidePricing={offer.hidePricing}>
      <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
        {!fetching && !!displayOfferPrice &&
          <Group direction="vertical">
            <PriceRowPriceCaption>
              {pluralizeToString('night', hotelNights)}{promoteAsBundle && ' + flights'} from
            </PriceRowPriceCaption>
            <LuxPlusPriceGuard
              showMemberPrice={(displayMemberPrice ?? 0) > 0}
              fallbackPrice={ <PriceRowPrice
                size="S"
                price={displayOfferPrice}
                saleUnit={saleUnit}
              />}
            >
              <PriceRowLuxPlusPrice
                size="S"
                memberPrice={displayMemberPrice ?? 0}
                saleUnit={saleUnit}
                logoPlacement="leading"
              />
            </LuxPlusPriceGuard>
            {shouldShowValue && shouldShowDiscountPercent && shownValue &&
              <PriceRowValueDiscountWithCaption
                size="M"
                originalValue={shownValue}
                discountPercentage={highestDiscountPercent}
              />
            }
          </Group>
        }
      </WalledContent>
    </OfferPageStickyFooter>
  )
}

const mapStateToProp = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: checkCanViewLuxPlusBenefits(state),
})

export default connect(mapStateToProp)(BedbankOfferPageStickyFooter)
